import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import moment from 'moment';
import React, { useEffect, useCallback, FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ButtonCmp from 'src/components/ButtonCmp';
import DateSelect from 'src/components/DateSelect/DateSelect';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { createDateWithoutTimezoneConversion, getDateWithSuffixFormat, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';
import * as yup from 'yup';

import { QUOTE_KEYS } from '../createQuote.const';
import { quoteDetails as quoteDetailsSelector, completedSteps as completedStepsSelector } from '../CreateQuote.slice';

import StepperAction from './StepperAction';
import { AVAILABILITY_OPTIONS, SENSITIVITY_OPTIONS, SENSITIVITY, SERVICE_TYPES, URGENCY_OPTIONS, FLEXIBLE_OPTIONS, PICKUP_SENSITIVITY_OPTIONS } from './Steps.const';
import { getDateCategory, getDayPairLabel, getWeekRangeLabel, isPickupDateToday as isPickupDateTodayHelper } from './Steps.helper';
import { useSaveNextStep } from './useSaveNextStep';

interface IQuoteDetails {
    setHeaderChildren?: (children: React.ReactNode) => void;
}

interface IQuoteDetailsForm {
    classification: string;
    urgency: string;
    pickupDate: Date | null;
    pickupSensitivity: string;
    pickupTimeFrame: string | null;
    deliveryDate: Date | null;
    deliverySensitivity: string;
}

const validationSchema = (t: TFunction, appearance: any) =>
    yup.object().shape({
        classification: yup.string().required(t('ValidationMessages.Service_Type_is_required')),
        urgency: yup.string().required(t('ValidationMessages.Urgency_is_required')),
        pickupDate: yup
            .string()
            .nullable()
            .required(t('ValidationMessages.Pickup_Date_is_required'))
            .test('not-in-past', t('QuotingHub.QuotingDashboard.Pickup_date_alert'), function (value) {
                if (!value) return true;

                const currentDateString = moment()
                    .tz(appearance?.timezone || 'UTC')
                    .format('YYYY-MM-DD');
                const pickupDateString = moment.utc(value).startOf('day').format('YYYY-MM-DD');

                const currentDate = moment(currentDateString);
                const pickupDate = moment(pickupDateString);

                return !pickupDate.isBefore(currentDate, 'day');
            }),
        pickupSensitivity: yup.string().required(t('QuotingHub.CreateQuote.pickup_sensitivity_required')),
        pickupTimeFrame: yup
            .string()
            .nullable()
            .when('pickupSensitivity', {
                is: (value: string) => value !== SENSITIVITY.RIGID && value !== SENSITIVITY.READY_NOW,
                then: (schema) => schema.required(t('QuotingHub.CreateQuote.pickupSensitivityValue_required')),
                otherwise: (schema) => schema.notRequired(),
            }),
        deliverySensitivity: yup.string().required(t('QuotingHub.CreateQuote.delivery_sensitivity_required')),
        deliveryDate: yup
            .string()
            .nullable()
            .when('deliverySensitivity', {
                is: (value: string) => value === SENSITIVITY.RIGID,
                then: (schema) => schema.required(t('QuotingHub.CreateQuote.delivery_date_is_required')),
                otherwise: (schema) => schema.notRequired(),
            })
            .test('not-before-pickup', t('QuotingHub.CreateQuote.delivery_date_error_message'), function (value) {
                const { pickupDate } = this.parent;
                if (!value || !pickupDate) return true;

                const pickup = moment.utc(pickupDate).startOf('day').format('YYYY-MM-DD');
                const delivery = moment.utc(value).startOf('day').format('YYYY-MM-DD');

                const pickupMoment = moment(pickup);
                const deliveryMoment = moment(delivery);

                return !deliveryMoment.isBefore(pickupMoment, 'day');
            }),
    });

const QuoteDetails: FC<IQuoteDetails> = ({ setHeaderChildren }) => {
    const { t } = useTranslation();
    const { appearance } = useAppSelector((state: RootState) => state.basic);
    const { onSaveNext } = useSaveNextStep();
    const quoteDetails = useAppSelector(quoteDetailsSelector);
    const completedSteps = useAppSelector(completedStepsSelector);
    const todayDate = useMemo(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC')), [appearance?.timezone]);

    const defaultValues = useMemo(() => {
        const defaultPickupSensitivity = quoteDetails?.pickupSensitivity || SENSITIVITY.READY_NOW;

        return {
            classification: quoteDetails?.classification || 'ftl',
            urgency: quoteDetails?.urgency || 'live',
            pickupDate: quoteDetails?.pickupDate || todayDate || '',
            pickupSensitivity: defaultPickupSensitivity,
            pickupTimeFrame:
                defaultPickupSensitivity === SENSITIVITY.RIGID || defaultPickupSensitivity === SENSITIVITY.READY_NOW ? null : quoteDetails?.pickupTimeFrame ?? FLEXIBLE_OPTIONS.TODAY_OR_TOMORROW,
            deliveryDate: quoteDetails?.deliveryDate || null,
            deliverySensitivity: quoteDetails?.deliverySensitivity || SENSITIVITY.FLEXIBLE,
        };
    }, [quoteDetails, todayDate]);

    const methods = useForm<IQuoteDetailsForm>({
        resolver: yupResolver(validationSchema(t, appearance)) as any,
        defaultValues,
    });

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        trigger,
        clearErrors,
        formState: { isSubmitted },
    } = methods;

    const onSubmit = (formData: IQuoteDetailsForm) => {
        const updatedFormData = {
            ...formData,
            pickupDate: moment.utc(formData.pickupDate).format('YYYY-MM-DD 00:00:00'),
            deliveryDate: formData.deliveryDate ? moment.utc(formData.deliveryDate).format('YYYY-MM-DD 00:00:00') : null,
        };

        // Ensure completedSteps is always treated as an array, defaulting to [] if undefined or not an array
        const completedStepsArray = Array.isArray(completedSteps) ? completedSteps : [];

        if (quoteDetails?.classification !== formData?.classification && completedStepsArray.length > 0) {
            // When classification changes, clear all steps after shipper/consignee since both
            // SHIPMENT_DETAILS (LTL step 3) and DIMENSIONS (LTL step 4) would be invalid for FTL and vice versa
            const localCompletedSteps = completedStepsArray.filter((step: string) => step !== QUOTE_KEYS.DIMENSIONS && step !== QUOTE_KEYS.SHIPMENT_DETAILS);
            onSaveNext(updatedFormData, QUOTE_KEYS.QUOTE_DETAILS, localCompletedSteps);
        } else {
            onSaveNext(updatedFormData, QUOTE_KEYS.QUOTE_DETAILS);
        }
    };

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) {
            return false;
        }

        // Update Redux with the form data
        handleSubmit(onSubmit)();

        return true;
    }, [trigger, handleSubmit]);

    useEffect(() => {
        setHeaderChildren?.(<StepperAction handleNext={handleSubmit(handleNext)} />);

        return () => {
            setHeaderChildren?.(null);
        };
    }, []);

    const classification = watch('classification');
    const pickupSensitivity = watch('pickupSensitivity');
    const deliveryDate = watch('deliveryDate');
    const pickupDate = watch('pickupDate');
    const pickupTimeFrame = watch('pickupTimeFrame');

    const isPickupDateToday = useMemo(() => isPickupDateTodayHelper(pickupDate, appearance?.timezone || 'UTC'), [pickupDate, appearance?.timezone]);

    const availablePickupSensitivityOptions = useMemo(() => {
        if (isPickupDateToday) {
            return PICKUP_SENSITIVITY_OPTIONS.filter((option) => option.value === SENSITIVITY.READY_NOW || option.value === SENSITIVITY.FLEXIBLE);
        } else {
            return PICKUP_SENSITIVITY_OPTIONS.filter((option) => option.value === SENSITIVITY.RIGID || option.value === SENSITIVITY.FLEXIBLE);
        }
    }, [isPickupDateToday]);

    const pickupTimeFrameOptions = useMemo(() => {
        if (!pickupDate) {
            return AVAILABILITY_OPTIONS;
        }

        const dateCategory = getDateCategory(pickupDate, appearance?.timezone || 'UTC');

        if (dateCategory === 'today') {
            // Today: "Today or Tomorrow", "Anytime this week", "Anytime next week"
            return AVAILABILITY_OPTIONS;
        } else if (dateCategory === 'current_week') {
            // Current week (not today): "Anytime this week", "Anytime next week"
            return AVAILABILITY_OPTIONS.filter((option) => option.value !== FLEXIBLE_OPTIONS.TODAY_OR_TOMORROW);
        } else if (dateCategory === 'next_week') {
            // Next week: "[Day] Xth or [Next Day] Yth", "Anytime next week"
            const dayPairLabel = getDayPairLabel(pickupDate);

            return [
                { label: dayPairLabel, value: `day_pair_${moment(pickupDate).format('YYYY-MM-DD')}` },
                { label: 'Anytime next week', value: FLEXIBLE_OPTIONS.NEXT_WEEK },
            ];
        } else {
            // Later: "[Day] Xth or [Next Day] Yth", "Anytime [week start] to [week end]"
            const dayPairLabel = getDayPairLabel(pickupDate);
            const weekRangeData = getWeekRangeLabel(pickupDate);

            return [
                { label: dayPairLabel, value: `day_pair_${moment(pickupDate).format('YYYY-MM-DD')}` },
                { label: weekRangeData.label, value: `week_range_${weekRangeData.startDate}` },
            ];
        }
    }, [pickupDate, appearance?.timezone]);

    // Update pickup sensitivity when classification or pickup date changes
    useEffect(() => {
        // Keep FLEXIBLE allowed for both FTL and LTL
        if (pickupSensitivity === SENSITIVITY.FLEXIBLE) {
            return;
        }
        const sensitivityValue = isPickupDateToday ? SENSITIVITY.READY_NOW : SENSITIVITY.RIGID;
        setValue('pickupSensitivity', sensitivityValue);
        setValue('pickupTimeFrame', null);

        if (isSubmitted) {
            trigger('pickupTimeFrame');
        }
    }, [classification, isPickupDateToday, isSubmitted, setValue, trigger]);

    // Reset pickupTimeFrame if "Today or Tomorrow" is selected but the date is not today
    useEffect(() => {
        if (!isPickupDateToday && pickupTimeFrame === FLEXIBLE_OPTIONS.TODAY_OR_TOMORROW) {
            setValue('pickupTimeFrame', null);

            if (isSubmitted) {
                trigger('pickupTimeFrame');
            }
        }
    }, [isPickupDateToday, pickupTimeFrame, isSubmitted, setValue, trigger]);

    // Reset pickupTimeFrame if it's no longer valid for the current pickup date
    useEffect(() => {
        if (pickupTimeFrame && pickupDate) {
            const validValues = pickupTimeFrameOptions.map((option) => option.value);

            if (!validValues.includes(pickupTimeFrame)) {
                setValue('pickupTimeFrame', null);

                if (isSubmitted) {
                    trigger('pickupTimeFrame');
                }
            }
        }
    }, [pickupDate, pickupTimeFrameOptions, pickupTimeFrame, isSubmitted, setValue, trigger]);

    const getMaximumPickupDate = () => {
        if (deliveryDate && moment(deliveryDate).isValid()) {
            return deliveryDate;
        }

        return undefined;
    };

    const getMinimumDate = useCallback(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC')), [appearance?.timezone]);

    const getMinimumDeliveryDate = (overridePickupSensitivityValue?: string, overridePickupDate?: Date | null) => {
        const currentPickupSensitivityValue = overridePickupSensitivityValue ?? pickupTimeFrame;
        const currentPickupDate = overridePickupDate ?? pickupDate;

        if (currentPickupDate && pickupSensitivity && currentPickupSensitivityValue) {
            if (pickupSensitivity === SENSITIVITY.FLEXIBLE || pickupSensitivity === SENSITIVITY.READY_NOW) {
                const dateObj = currentPickupDate instanceof Date ? currentPickupDate : new Date(currentPickupDate);
                const pickupMoment = moment.utc([dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()]);

                // Handle dynamic day pair values (e.g., "day_pair_2025-02-03")
                if (currentPickupSensitivityValue.startsWith('day_pair_')) {
                    // For day pair, minimum delivery date is the same as the pickup date
                    return createDateWithoutTimezoneConversion(pickupMoment.clone());
                }

                // Handle dynamic week range values (e.g., "week_range_2025-02-03")
                if (currentPickupSensitivityValue.startsWith('week_range_')) {
                    // For week range, minimum delivery date is the start of the week after the pickup date's week
                    return createDateWithoutTimezoneConversion(pickupMoment.clone().add(1, 'week').startOf('week'));
                }

                switch (currentPickupSensitivityValue) {
                    case FLEXIBLE_OPTIONS.ANYTIME_THIS_WEEK:
                        return createDateWithoutTimezoneConversion(pickupMoment.clone().add(1, 'week').startOf('week'));
                    case FLEXIBLE_OPTIONS.NEXT_WEEK:
                        return createDateWithoutTimezoneConversion(pickupMoment.clone().add(2, 'weeks').startOf('week'));
                    default:
                        return currentPickupDate;
                }
            }
        } else if ((currentPickupDate && !currentPickupSensitivityValue) || (currentPickupDate && !pickupSensitivity)) {
            return currentPickupDate;
        } else {
            return getMinimumDate();
        }
    };

    const handleDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);
    };

    const handlePickupDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);

        const newMinimumDeliveryDate = getMinimumDeliveryDate(undefined, value);

        if (deliveryDate && newMinimumDeliveryDate && moment.utc(deliveryDate).isBefore(newMinimumDeliveryDate, 'day')) {
            setValue('deliveryDate', null);

            if (isSubmitted) {
                trigger('deliveryDate');
            }
        }
    };

    const handleOptionChange =
        (optionVal: string, onChange: any, pickupSensitivityTypeChange: boolean = false) =>
        () => {
            onChange(optionVal);

            if (pickupSensitivityTypeChange) {
                const newMinimumDeliveryDate = getMinimumDeliveryDate(optionVal);

                if (deliveryDate && moment.utc(deliveryDate).isBefore(newMinimumDeliveryDate, 'day')) {
                    setValue('deliveryDate', null);

                    if (isSubmitted) {
                        trigger('deliveryDate');
                    }
                }
            }
        };

    const handleSensitivityChange = (sensitivityVal: string, onChange: any, sensitivityType?: string) => () => {
        if ((sensitivityVal === SENSITIVITY.RIGID || sensitivityVal === SENSITIVITY.READY_NOW) && sensitivityType) {
            setValue(sensitivityType as keyof IQuoteDetailsForm, null, { shouldValidate: isSubmitted });

            if (isSubmitted) {
                setTimeout(() => {
                    trigger(sensitivityType as keyof IQuoteDetailsForm);
                }, 0);
            }
        }

        onChange(sensitivityVal);

        if (isSubmitted && !sensitivityType) {
            if (sensitivityVal === SENSITIVITY.FLEXIBLE) {
                clearErrors('deliveryDate');
            } else if (sensitivityVal === SENSITIVITY.RIGID) {
                trigger('deliveryDate');
            }
        }
    };

    return (
        <>
            {/* Service Type */}
            <div className="stepper-content-container ">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('QuotingHub.CreateQuote.Service_Type')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="classification"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-wrap gap-2">
                                {SERVICE_TYPES.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={handleOptionChange(option.value, onChange)}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Urgency */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('QuotingHub.CreateQuote.Urgency')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="urgency"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-wrap gap-2">
                                {URGENCY_OPTIONS.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={handleOptionChange(option.value, onChange)}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Pickup Date */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('QuotingHub.CreateQuote.Pickup_Date')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="pickupDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <>
                                <DateSelect
                                    label=""
                                    inputName="pickupDate"
                                    className={`form_control`}
                                    errorText={error?.message ? true : false}
                                    placeholder="Select pickup date"
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC'))}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    maxDate={getMaximumPickupDate()}
                                    onChangeFunc={handlePickupDateChange(onChange)}
                                    parentClassName="w-full"
                                    isDisplayIcon={true}
                                />
                            </>
                        )}
                    />
                </div>
            </div>

            {/* Pickup Rigidity */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('QuotingHub.CreateQuote.Pickup_Sensitivity')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="pickupSensitivity"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-wrap gap-2">
                                {availablePickupSensitivityOptions.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={handleSensitivityChange(option.value, onChange, 'pickupTimeFrame')}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />

                    <Controller
                        name="pickupTimeFrame"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <SelectBox
                                options={pickupTimeFrameOptions}
                                value={value ? pickupTimeFrameOptions.find((option) => option.value === value) : null}
                                onChangeFunc={(selectedOption: any) => {
                                    handleOptionChange(selectedOption?.value, onChange, true)();
                                }}
                                className="w-full"
                                isError={!!error}
                                isDisabled={pickupSensitivity === SENSITIVITY.RIGID || pickupSensitivity === SENSITIVITY.READY_NOW}
                                placeholder={t('QuotingHub.CreateQuote.Select_Pickup_Sensitivity')}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Target Delivery Date */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('QuotingHub.CreateQuote.Target_Delivery_Date')}</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="deliveryDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                label=""
                                inputName="deliveryDate"
                                className={`form_control `}
                                placeholder="Select delivery date"
                                dateFormat="dddd, MMMM Do, YYYY"
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                minDate={getMinimumDeliveryDate()}
                                onChangeFunc={handleDateChange(onChange)}
                                parentClassName="w-full"
                                isDisplayIcon={true}
                                errorText={error?.message ? true : false}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Delivery Sensitivity */}
            <div className="border-t pb-5 border-utilityGray200 stepper-content-container border-b">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('QuotingHub.CreateQuote.Delivery_Sensitivity')}</label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="deliverySensitivity"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex gap-2">
                                {SENSITIVITY_OPTIONS.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={handleSensitivityChange(option.value, onChange)}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>
        </>
    );
};

export default QuoteDetails;
